import React, { useState } from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import CTA from '../components/sections/CTA';
import { Link } from 'react-router-dom';
import { Video, Mic, MessageCircle, FileText, CheckCircle2, BookOpen, PenLine, TrendingUp } from 'lucide-react';
import HowItWorksDiagram from '../assets/how-it-works.jpeg';

const VIDEO_URL = 'https://firebasestorage.googleapis.com/v0/b/images-1aaab.appspot.com/o/20546634-uhd_3840_2160_30fps.mp4?alt=media&token=43e2bb92-2523-416f-a0b3-a1c42a351537';

const sessionTypes = [
  { icon: Video,         type: 'Video Session',  desc: 'Face-to-face consultation with screen sharing. Best for in-depth reviews, presentations and planning sessions.',      color: 'bg-forest-50 border-forest-200 text-forest-700' },
  { icon: Mic,           type: 'Audio Session',  desc: 'Voice-only calls — ideal for quick Q&A, advice sessions or when you\'re on the go.',                                color: 'bg-blue-50 border-blue-200 text-blue-700' },
  { icon: MessageCircle, type: 'Chat Session',   desc: 'Text-based consultation with file sharing. Great for document reviews, feedback and structured written guidance.',   color: 'bg-purple-50 border-purple-200 text-purple-700' },
  { icon: FileText,           type: 'PDF Transcript',    desc: 'PDF generated transcripts for records',   color: 'bg-purple-50 border-purple-200 text-purple-700' },
];

const gates = [
  { icon: BookOpen,   gate: 'Gate 1', title: 'Complete All Content',   desc: 'Watch and complete all video lessons and documents in your current level.',   color: 'bg-amber-50 border-amber-200' },
  { icon: Video,      gate: 'Gate 2', title: 'Attend a Live Session',   desc: 'Book and attend a live session with a verified expert covering your level.',  color: 'bg-forest-50 border-forest-200' },
  { icon: PenLine,    gate: 'Gate 3', title: 'Pass the Canvas',         desc: 'Submit your business assignment and receive a passing grade from your expert.',color: 'bg-blue-50 border-blue-200' },
  { icon: TrendingUp, gate: '🎉',    title: 'Advance to Next Level',   desc: 'All 3 gates passed? Congratulations — you unlock the next level of your journey.', color: 'bg-gold-50 border-gold-200' },
];

const faqs = [
  { q: 'How do I book a session?',              a: 'Browse experts, click their profile, choose a session type and pick a time from their availability calendar. Your booking request goes to the expert for confirmation.' },
  { q: 'What happens after I book?',            a: 'The expert receives your request and either confirms or declines. Once confirmed, a session room is created and you receive a link via SMS and in-app notification.' },
  { q: 'Can I reschedule a booking?',           a: 'Yes — up to 2 times per booking. Go to My Bookings → View Details → Reschedule. The expert will need to re-confirm the new time.' },
  { q: 'How does the level system work?',       a: 'Each level has 3 completion gates: content, session and canvas. Complete all 3 to advance to the next level. You progress at your own pace.' },
  { q: 'How long does a session last?',         a: 'Sessions can be 30, 45 or 60 minutes. You choose the duration when booking. Cost is calculated based on the expert\'s hourly rate.' },
  { q: 'Are sessions recorded?',                a: 'Video and audio sessions can be recorded with both parties\' consent. After the session, a transcript is automatically generated using AI.' },
];

const HowItWorksPage = () => {
  const [videoReady, setVideoReady] = useState(false);

  return (
    <div className="min-h-screen bg-cream-50">
      <Navbar />

      {/* ── Video Hero ── */}
      <div className="relative" style={{ minHeight: 380 }}>
        <video
          autoPlay muted loop playsInline
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
          style={{ zIndex: 0, opacity: videoReady ? 1 : 0 }}
          onCanPlay={() => setVideoReady(true)}
        >
          <source src={VIDEO_URL} type="video/mp4" />
        </video>
        {!videoReady && (
          <div className="absolute inset-0" style={{ zIndex: 0, background: '#0a1e0d' }} />
        )}
        <div className="absolute inset-0"
          style={{ zIndex: 1, background: 'linear-gradient(160deg, rgba(2,10,5,0.82) 0%, rgba(3,14,7,0.78) 50%, rgba(1,8,3,0.88) 100%)' }} />
        <div className="absolute top-0 left-0 right-0 h-[3px]"
          style={{ zIndex: 2, background: 'linear-gradient(90deg, #dc2626, #d97706, #16a34a, #d97706, #dc2626)' }} />

        <div className="relative z-10 pt-32 pb-20 px-6">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold uppercase tracking-widest mb-6"
              style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              How It Works
            </div>
            <h1 className="font-serif text-4xl lg:text-6xl font-semibold text-white leading-tight mb-6">
              Your path to <br />
              <span className="italic font-light text-green-300">entrepreneurship success</span>
            </h1>
            <p className="text-white/55 text-lg leading-relaxed">
              From finding the right expert to completing your capacity building journey — here's everything you need to know.
            </p>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-16"
          style={{ zIndex: 3, background: 'linear-gradient(to bottom, transparent, #f8f6f0)' }} />
      </div>

      {/* Diagram */}
      <img src={HowItWorksDiagram} alt="How It Works Diagram" className="mx-auto my-20" />

      {/* Session types */}
      <div className="py-20 max-w-5xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <div className="section-label justify-center mb-4">Session Types</div>
          <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-forest-900">
            Choose how you <span className="italic text-forest-600">connect</span>
          </h2>
          <p className="text-forest-500 text-sm mt-3 max-w-lg mx-auto">
            All session types include PDF generated transcripts after the session ends.
          </p>
        </div>
        <div className="grid sm:grid-cols-4 gap-6">
          {sessionTypes.map((s, i) => (
            <div key={i} className={`border rounded-2xl p-6 ${s.color}`}>
              <s.icon size={28} className="mb-4" />
              <h3 className="font-serif font-semibold text-forest-900 text-lg mb-2">{s.type}</h3>
              <p className="text-sm text-forest-600 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Level completion gates */}
      <div className="bg-forest-900 py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-gold-400 mb-4">
              <span className="w-2 h-2 rounded-full bg-gold-400" /> Level Completion
            </div>
            <h2 className="font-serif text-3xl lg:text-4xl font-semibold text-cream-50">
              The 3-gate <span className="italic text-gold-400">system</span>
            </h2>
            <p className="text-forest-300 text-sm mt-3 max-w-lg mx-auto">
              Every level has 3 gates. Complete all 3 to advance to the next level.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {gates.map((g, i) => (
              <div key={i} className={`${g.color} border rounded-2xl p-5`}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <g.icon size={16} className="text-forest-700" />
                  </div>
                  <span className="text-xs font-bold text-forest-500 uppercase tracking-widest">{g.gate}</span>
                </div>
                <h3 className="font-serif font-semibold text-forest-900 text-base mb-1.5">{g.title}</h3>
                <p className="text-xs text-forest-600 leading-relaxed">{g.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQs */}
      <div className="py-20 max-w-3xl mx-auto px-6 lg:px-10">
        <div className="text-center mb-12">
          <div className="section-label justify-center mb-4">FAQ</div>
          <h2 className="font-serif text-3xl font-semibold text-forest-900">
            Frequently asked <span className="italic text-forest-600">questions</span>
          </h2>
        </div>
        <div className="space-y-4">
          {faqs.map((f, i) => (
            <div key={i} className="bg-white border border-cream-200 rounded-2xl p-5">
              <div className="flex items-start gap-3">
                <CheckCircle2 size={16} className="text-forest-500 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-sans font-semibold text-forest-900 text-sm mb-1.5">{f.q}</h4>
                  <p className="text-sm text-forest-600 leading-relaxed">{f.a}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="pb-8 text-center">
        <Link to="/experts" className="btn-primary inline-flex text-base px-8 py-4">
          Find an Expert →
        </Link>
      </div>

      <CTA />
      <Footer />
    </div>
  );
};

export default HowItWorksPage;